import React, { useState, useEffect } from "react";
import Header from "./Header";
import VertNav from "./VertNav";
import ExpenseModal from "./ExpenseModal.jsx";
import config from "../../config.js";

const ExpensesManagement = () => {
    const [expensesData, setExpensesData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const expensesPerPage = 15;
    const bid = localStorage.getItem("branch_id");

    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1
    );
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [showModal, setShowModal] = useState(false);

    const years = React.useMemo(() => {
        if (!expensesData.length) {
            const currentYear = new Date().getFullYear();
            return [currentYear - 1, currentYear, currentYear + 1];
        }
        return [
            ...new Set(expensesData.map((expense) => parseInt(expense.year))),
        ].sort((a, b) => a - b);
    }, [expensesData]);

    useEffect(() => {
        const fetchExpenses = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(
                    `${config.apiUrl}/api/swalook/expense_management/?branch_name=${bid}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Token ${token}`,
                        },
                    }
                );
                if (!response.ok) throw new Error("Failed to fetch data");
                const result = await response.json();
                if (result.status) {
                    setExpensesData(result.data);
                    setFilteredData(result.data);
                } else {
                    throw new Error("Invalid response");
                }
            } catch (error) {
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchExpenses();
    }, []);

    useEffect(() => {
        if (!Array.isArray(expensesData)) {
            setFilteredData([]);
            return;
        }

        const filtered = expensesData.filter(
            (expense) =>
                parseInt(expense.month) === selectedMonth &&
                parseInt(expense.year) === selectedYear
        );

        setFilteredData(filtered);
        setCurrentPage(1);
    }, [selectedMonth, selectedYear, expensesData]);

    const indexOfLastExpense = currentPage * expensesPerPage;
    const indexOfFirstExpense = indexOfLastExpense - expensesPerPage;
    const currentExpenses = Array.isArray(filteredData)
        ? filteredData.slice(indexOfFirstExpense, indexOfLastExpense)
        : [];

    const totalPages = Math.ceil(filteredData.length / expensesPerPage);
    const pageNumbers = [...Array(totalPages).keys()].map((i) => i + 1);

    const handlePageChange = (pageNum) => {
        setCurrentPage(pageNum);
    };

    return (
        <>
            <VertNav />
            <Header />
            <div className="flex min-h-screen bg-gray-50">
                <div className="flex-grow md:ml-72 lg:ml-72 p-4 md:p-6 lg:p-10 overflow-auto">
                    <div className="bg-white rounded-3xl shadow-md p-8">
                        <div className="md:flex justify-between items-center mb-8">
                            <h2 className="text-2xl font-semibold text-gray-800">
                                Expenses
                            </h2>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <label
                                        htmlFor="month-filter"
                                        className="text-gray-500"
                                    >
                                        Filter:
                                    </label>
                                    <select
                                        id="month-filter"
                                        className="px-3 py-2 border border-gray-300 rounded-full"
                                        value={selectedMonth}
                                        onChange={(e) =>
                                            setSelectedMonth(
                                                parseInt(e.target.value)
                                            )
                                        }
                                    >
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <option key={i + 1} value={i + 1}>
                                                {new Date(0, i).toLocaleString(
                                                    "default",
                                                    { month: "long" }
                                                )}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        id="year-filter"
                                        className="px-3 py-2 border border-gray-300 rounded-full"
                                        value={selectedYear}
                                        onChange={(e) =>
                                            setSelectedYear(
                                                parseInt(e.target.value)
                                            )
                                        }
                                    >
                                        {years.map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full transition-colors"
                                    onClick={() => setShowModal(true)}
                                >
                                    + New Expense
                                </button>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="flex justify-center items-center h-64">
                                Loading...
                            </div>
                        ) : error ? (
                            <div className="text-red-500">Error: {error}</div>
                        ) : (
                            <div className="overflow-x-auto  rounded-[2.5rem]">
                                <table className="w-full rounded-[2.5rem] border border-gray-200">
                                    <thead className="bg-gray-50 ">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                S. No.
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Expense Category
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Expense Account
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Total Amount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Amount Paid
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Due Amount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Due Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                                Notes
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {currentExpenses.map(
                                            (expense, index) => (
                                                <tr
                                                    key={expense.id}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {indexOfFirstExpense +
                                                            index +
                                                            1}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {expense.date
                                                            .split("-")
                                                            .reverse()
                                                            .join("-")}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {expense.expense_category &&
                                                        expense.expense_category
                                                            .length > 0
                                                            ? expense.expense_category
                                                                  .map((cat) =>
                                                                      JSON.parse(
                                                                          cat.vendor_expense_type.replace(
                                                                              /'/g,
                                                                              '"'
                                                                          )
                                                                      ).join(
                                                                          ", "
                                                                      )
                                                                  )
                                                                  .join(", ")
                                                            : "N/A"}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {
                                                            expense.expense_account
                                                        }
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {expense.expense_amount}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {expense.amount_paid ||
                                                            "-"}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {expense.due_amount ||
                                                            "-"}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {expense.due_date ||
                                                            "-"}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200">
                                                        {expense.comment || "-"}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row justify-between items-center mt-6">
                            <span className="text-sm text-gray-500 mb-4 sm:mb-0">
                                Showing data {indexOfFirstExpense + 1} to{" "}
                                {Math.min(
                                    indexOfLastExpense,
                                    filteredData.length
                                )}{" "}
                                of {filteredData.length} entries
                            </span>
                            <div className="flex gap-2">
                                {pageNumbers.map((number) => (
                                    <button
                                        key={number}
                                        className={`px-3 py-1 rounded-full ${
                                            number === currentPage
                                                ? "bg-blue-500 text-white"
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                        onClick={() => handlePageChange(number)}
                                    >
                                        {number}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {showModal && (
                    <ExpenseModal onClose={() => setShowModal(false)} />
                )}
            </div>
        </>
    );
};

export default ExpensesManagement;
